import { ICS, ICS_FLAGS, type FlagElement, type FlagSpec } from "@nav-games/flags";

interface FlagProps {
  char: string;
  size?: number;
  /// When true and the flag is a pennant, the swallowtail / tapered
  /// edge is drawn. Set to false for a "card" view that ignores the
  /// pennant cut and just shows the design as a rectangle.
  honourShape?: boolean;
}

/// Render an ICS flag at any size. Uses the FlagSpec data from
/// `@nav-games/flags` — no JSX shipped from the package per the
/// project's "no DOM in packages" rule.
export function Flag({ char, size = 160, honourShape = true }: FlagProps) {
  const spec: FlagSpec | undefined = ICS_FLAGS[char.toUpperCase()] ?? ICS_FLAGS[char];
  if (!spec) {
    return (
      <div
        style={{
          width: size,
          height: size,
          background: "#eee",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "monospace",
        }}
      >
        ?
      </div>
    );
  }

  const isPennant = honourShape && spec.pennant === true;
  const isSwallowtail = honourShape && spec.swallowtail === true;
  const isTriangular = honourShape && spec.triangular === true;
  // ICS numeric / answering pennants are long tapered tongues
  // — full-height hoist on the left, narrowing through the body to
  // a SQUARED fly end with a triangular notch cut out (the
  // "swallowtail" / fishtail typical of ICS numeric pennants).
  // 2:1 aspect roughly matches the elongated proportions shown in
  // standard manufacturer catalogues (e.g. marinelite.gr).
  const pennantLen = 200;
  // Fly geometry: the squared edge runs from y=pennantFlyTop to
  // y=pennantFlyBottom (a 30-unit vertical edge in viewBox units),
  // with a triangular V-notch cut inward from that edge to
  // (pennantNotchX, 50). Numbers picked so the notch reads cleanly
  // at small sizes — too shallow and it looks like a manufacturing
  // defect; too deep and the pennant reads as a swallowtail letter
  // flag.
  const pennantFlyTop = 35;
  const pennantFlyBottom = 65;
  const pennantNotchX = pennantLen - 22;
  const w = isPennant ? size * (pennantLen / 100) : size;
  const h = size;

  // Build a clipPath if needed.
  const clipId = `flag-clip-${char}`;
  const useClip = isPennant || isSwallowtail || isTriangular;
  // Outline silhouette — drawn AFTER the fill so it sits on top of
  // any edge details. Same shape as the clip path; stroke width is
  // in viewBox units. Expanded viewBox below absorbs the stroke
  // half-width so the silhouette stays at the natural edge.
  // Letter flags use the swallowtail clip (V-cut on a rectangular
  // flag); substitute flags are plain isoceles triangles.
  const pennantOutline = `0,0 ${pennantLen},${pennantFlyTop} ${pennantNotchX},50 ${pennantLen},${pennantFlyBottom} 0,100`;
  const outlinePoints = isPennant
    ? pennantOutline
    : isSwallowtail
      ? "0,0 100,0 80,50 100,100 0,100"
      : isTriangular
        ? "0,0 100,50 0,100"
        : null;
  const stroke = "#0f172a";
  const strokeW = 1.2;
  const pad = strokeW / 2 + 0.2;
  const vbX = -pad;
  const vbY = -pad;
  const vbW = (isPennant ? pennantLen : 100) + pad * 2;
  const vbH = 100 + pad * 2;

  return (
    <svg
      width={w}
      height={h}
      viewBox={`${vbX} ${vbY} ${vbW} ${vbH}`}
      aria-label={`ICS flag ${char}`}
      role="img"
    >
      {useClip && (
        <defs>
          <clipPath id={clipId}>
            {isPennant ? (
              // Tapered pennant — full-height hoist on the left,
              // narrowing through the body to a squared fly end
              // with a V-notch cut out (fishtail). Used for ICS
              // numeric pennants 0-9 and the Code / Answering
              // pennant.
              <polygon points={pennantOutline} />
            ) : isTriangular ? (
              // Right-triangle pennant — hoist on the left, fly point
              // at (100, 50). Used by the three substitute flags.
              <polygon points="0,0 100,50 0,100" />
            ) : (
              // Swallowtail — V-cut into the right edge.
              <polygon points="0,0 100,0 80,50 100,100 0,100" />
            )}
          </clipPath>
        </defs>
      )}
      <g clipPath={useClip ? `url(#${clipId})` : undefined}>
        {/* Background fill must cover the FULL clipped region, not
            just the 0–100 hoist square. For pennants this matters:
            the design (stripes, discs, crosses) lives in the 0–100
            zone, but the bg colour has to extend across the taper
            all the way to the fly point. Without this, the taper
            renders as the SVG's transparent background (looking
            like a white triangle stuck onto the fly end). */}
        <rect
          x={0}
          y={0}
          width={isPennant ? pennantLen : 100}
          height={100}
          fill={ICS[spec.bg]}
        />
        {spec.elements.map((el, i) =>
          renderElement(el, i, isPennant ? pennantLen : null),
        )}
      </g>
      {/* Silhouette outline — keeps white-on-white flag fields
          (Alfa, Sierra, Tango, etc.) visible against a light page
          background. Drawn outside the clip so the edges read
          cleanly. */}
      {outlinePoints ? (
        <polygon
          points={outlinePoints}
          fill="none"
          stroke={stroke}
          strokeWidth={strokeW}
          strokeLinejoin="round"
        />
      ) : (
        <rect
          x={0}
          y={0}
          width={100}
          height={100}
          fill="none"
          stroke={stroke}
          strokeWidth={strokeW}
        />
      )}
    </svg>
  );
}

function renderElement(
  el: FlagElement,
  key: number,
  /// When set (pennant rendering), rects that reach the right edge
  /// of the 0–100 hoist square get their width extended out to this
  /// fly-end x coordinate. This is how horizontal stripes (pennant
  /// 4's white fess), full-width halves (pennants 3 / 6 / 7), and
  /// the right-side blocks of vertical splits (pennant 5, pennant
  /// 9's right quarters) extend correctly through the taper. Local
  /// design features (vertical pales, discs, saltires) leave their
  /// rects untouched because they don't touch the right edge.
  pennantExtendX: number | null,
): React.ReactElement {
  switch (el.type) {
    case "rect": {
      let { w } = el;
      if (
        pennantExtendX !== null &&
        el.x + el.w >= 100 - 0.01 /* tolerate floating-point */
      ) {
        w = pennantExtendX - el.x;
      }
      return <rect key={key} x={el.x} y={el.y} width={w} height={el.h} fill={ICS[el.fill]} />;
    }
    case "polygon":
      return <polygon key={key} points={el.points} fill={ICS[el.fill]} />;
    case "circle":
      return <circle key={key} cx={el.cx} cy={el.cy} r={el.r} fill={ICS[el.fill]} />;
    case "line":
      return (
        <line
          key={key}
          x1={el.x1}
          y1={el.y1}
          x2={el.x2}
          y2={el.y2}
          stroke={ICS[el.stroke]}
          strokeWidth={el.sw}
        />
      );
    case "group":
      return (
        <g key={key} transform={el.transform}>
          {el.children.map((c, i) => renderElement(c, i, pennantExtendX))}
        </g>
      );
  }
}
